"use client";

import Link from "next/link";
import { Fragment, ReactNode } from "react";

import { useRegisterMutation } from "@saas/account/data-access";
import {
  registerLoyaltyValidation,
  TRegisterLoyaltyFormValues,
} from "@saas/account/utils/@validation/register-loyalty";
import {
  getProfileFromSession,
  SessionProfile,
} from "@saas/account/utils/get-profile-from-session/get-profile-from-session";
import { env } from "@saas/config/shared/env/env";
import { useAlert } from "@saas/shared/feature";
import { BaseButton } from "@saas/shared/ui/base-button/base-button";
import { BaseInput } from "@saas/shared/ui/base-input/base-input";
import { CheckboxInput } from "@saas/shared/ui/checkbox-input/checkbox-input";
import { DateInput } from "@saas/shared/ui/date-input/date-input";
import { PhoneInput } from "@saas/shared/ui/phone-input/phone-input";
import { PasswordInput } from "@saas/shared/ui/text-field/password-input/password-input";
import { notify } from "@saas/shared/utils/alert/alert";
import { classNames } from "@saas/shared/utils/class-names/class-names";
import { formatQueryDate } from "@saas/shared/utils/format-query-date/format-query-date";

import { RegistrationFlow, UiNode, UiNodeInputAttributes } from "@ory/client";
import { Controller, useForm, useFormState } from "react-hook-form";

export interface ILoyaltyRegisterFormProps {
  onSuccess: (profile: SessionProfile | null) => void;
  testid?: string;
}

export const LoyaltyRegisterForm = ({
  testid = "loyalty-register-form",
  onSuccess: handleSuccess,
}: ILoyaltyRegisterFormProps) => {
  const [, alertDispatch] = useAlert();

  const { control, register, getValues, setError, handleSubmit } =
    useForm<TRegisterLoyaltyFormValues>({
      mode: "onChange",
      defaultValues: {
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        isTncChecked: false,
      },
    });

  const { isValid, errors } = useFormState({ control });

  const { mutate: mutateRegister, isReady, isLoading } = useRegisterMutation();

  const handleRegister = (data: TRegisterLoyaltyFormValues) => {
    mutateRegister(
      {
        name: data.name,
        identifier: data.email,
        password: data.password,
        phone: data.phone,
        birthdate: data.birthdate,
      },
      {
        onSuccess: (res) => {
          const session = res.data.session;

          handleSuccess(getProfileFromSession(session));
        },
        onError: (error) => {
          const submitError = (error as RegistrationFlow)?.ui?.messages?.[0];
          const formError = (error as RegistrationFlow)?.ui
            ?.nodes as ReadonlyArray<
            UiNode & {
              attributes: UiNodeInputAttributes;
            }
          >;

          if (formError) {
            const fieldError = formError?.filter(
              (field) => field.messages.length > 0
            );

            fieldError.forEach((field) => {
              if (field.attributes.name === "traits.email") {
                return setError("email", {
                  type: "manual",
                  message: field.messages[0].text,
                });
              }

              if (field.attributes.name === "password") {
                if (field.messages[0].id === 4000005) {
                  return setError("password", {
                    type: "manual",
                    message:
                      "Maaf, password terlalu mirip dengan identitas anda (nama, email, phone).",
                  });
                }

                return setError("password", {
                  type: "manual",
                  message: field.messages[0].text,
                });
              }

              if (field.attributes.name === "traits.name") {
                return setError("email", {
                  type: "manual",
                  message: field.messages[0].text,
                });
              }

              if (field.attributes.name === "traits.phone") {
                return setError("phone", {
                  type: "manual",
                  message: field.messages[0].text,
                });
              }
            });
          }

          if (submitError) {
            if (submitError.id === 4000007) {
              const errorMessage = {
                type: "manual",
                message:
                  "Data yang dimasukkan tidak dapat digunakan. Silakan coba yang lain atau hubungi CS jika membutuhkan bantuan lebih lanjut.",
              };

              return setError("email", errorMessage);
            }

            return notify(alertDispatch, {
              content: submitError.text,
              variant: "negative",
            });
          }
        },
      }
    );
  };

  return (
    <form
      className={"flex flex-col gap-6"}
      onSubmit={handleSubmit(handleRegister)}
    >
      <div className={"flex flex-col gap-3"}>
        <InputField label={"Nama Kamu"} testid={`${testid}__input__name`}>
          <BaseInput
            label={"Isi nama"}
            placeholder={"Nama kamu"}
            className={"placeholder:text-le"}
            showLabel={false}
            error={!!errors.name}
            testid={`${testid}__input__name__value`}
            hint={errors.name?.message}
            {...register("name", {
              required: "Wajib diisi.",
            })}
          />
        </InputField>

        <InputField label={"Email"} testid={`${testid}__input__email`}>
          <BaseInput
            type={"email"}
            label={"Isi email"}
            placeholder={"Email kamu"}
            className={"placeholder:text-le"}
            showLabel={false}
            error={!!errors.email}
            hint={errors.email?.message}
            testid={`${testid}__input__email__value`}
            {...register("email", {
              required: "Wajib diisi.",
              validate: registerLoyaltyValidation.email,
            })}
          />
        </InputField>

        <InputField label={"Kata Sandi"} testid={`${testid}__input__password`}>
          <PasswordInput
            label={"Isi kata sandi"}
            placeholder={"Kata sandi"}
            className={"placeholder:text-le"}
            showLabel={false}
            error={!!errors.password}
            hint={errors.password?.message}
            testid={`${testid}__input__password__value`}
            {...register("password", {
              required: "Wajib diisi.",
              validate: registerLoyaltyValidation.password,
              deps: getValues("confirmPassword") ? ["confirmPassword"] : [],
            })}
          />
        </InputField>

        <InputField
          label={"Konfirmasi Kata Sandi"}
          testid={`${testid}__input__confirm-password`}
        >
          <PasswordInput
            label={"Isi konfirmasi kata sandi"}
            placeholder={"Konfirmasi kata sandi"}
            className={"placeholder:text-le"}
            showLabel={false}
            error={!!errors.confirmPassword}
            hint={errors.confirmPassword?.message}
            testid={`${testid}__input__confirm-password__value`}
            {...register("confirmPassword", {
              required: "Wajib diisi.",
              validate: (value) =>
                registerLoyaltyValidation.confirmPassword(
                  value,
                  getValues("password")
                ),
              deps: ["password"],
            })}
          />
        </InputField>

        <InputField
          label={"Nomor Telepon"}
          testid={`${testid}__input__phone`}
          description={"Pastikan nomor telepon sudah terdaftar Whatsapp."}
        >
          <Controller
            control={control}
            name={"phone"}
            rules={{
              required: "Wajib diisi.",
              validate: registerLoyaltyValidation.phone,
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <PhoneInput
                value={value}
                showLabel={false}
                label={"Nomor Telepon"}
                placeholder={"Masukkan nomor telepon"}
                inputMode={"tel"}
                hint={error?.message}
                error={!!error?.message}
                onChange={onChange}
                testid={`${testid}__input__phone__value`}
              />
            )}
          />
        </InputField>

        <InputField
          label={"Tanggal Lahir (Opsional)"}
          testid={`${testid}__input__birthdate`}
          isRequired={false}
        >
          <Controller
            control={control}
            name={"birthdate"}
            render={({ field: { value, onChange } }) => {
              return (
                <DateInput
                  label={"Birth Date"}
                  type={"date"}
                  value={value}
                  onChange={onChange}
                  inputClassName={classNames(!value && "!text-le")}
                  showLabel={false}
                  testid={`${testid}__input__birthdate__value`}
                  max={formatQueryDate(new Date())}
                />
              );
            }}
          />
        </InputField>

        <div className={"flex items-start gap-2"}>
          <Controller
            name={"isTncChecked"}
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <CheckboxInput
                id={"tnc"}
                label={"TnC"}
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                showLabel={false}
                testid={`${testid}__checkbox__terms-condition`}
              />
            )}
          />

          <div
            className={"label-medium text-he"}
            data-testid={`${testid}__text__terms-condition`}
          >
            Dengan melanjutkan pendaftaran, kamu setuju dengan{" "}
            {tncLinks.map(({ label, href }, i) => {
              const isSecondToLast = i === tncLinks.length - 2;
              const isLast = i === tncLinks.length - 1;

              return (
                <Fragment key={label}>
                  <Link href={href} target={"_blank"} rel={"noopener"}>
                    <span className={"text-button cursor-pointer underline"}>
                      {label}
                    </span>
                  </Link>

                  {!isLast && (
                    <span className={"text-button"}>
                      {isSecondToLast ? " & " : ", "}
                    </span>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <BaseButton
        type={"submit"}
        variant={"primary"}
        className={"w-full"}
        disabled={!isReady || !isValid}
        testid={`${testid}__button__register`}
        isLoading={isLoading}
      >
        Daftar Akun
      </BaseButton>
    </form>
  );
};

type InputFieldProps = {
  label: string;
  description?: string;
  isRequired?: boolean;
  testid: string;
  children: ReactNode;
};

const InputField = ({
  testid,
  label,
  isRequired = true,
  description,
  children,
}: InputFieldProps) => {
  return (
    <div>
      <label
        className={"body-b2 mb-2 font-semibold"}
        data-testid={`${testid}__label`}
      >
        <span className={"text-he"}>{label}</span>
        {isRequired ? <span className={"text-error"}>*</span> : null}
      </label>

      {description ? (
        <p className={"body-b3 text-le mb-2"} data-testid={`${testid}__note`}>
          {description}
        </p>
      ) : null}

      {children}
    </div>
  );
};

const tncLinks = [
  {
    label: "Ketentuan Penggunaan",
    href: env.TNC_URL,
  },
  {
    label: "Kode Etik Penjual",
    href: env.CATALOG_POLICY_URL,
  },
  {
    label: "Kebijakan Privasi",
    href: env.PRIVACY_POLICY_URL,
  },
  {
    label: "Ketentuan Pembatalan dan Pengembalian Melaka",
    href: env.RETURN_POLICY_URL,
  },
  {
    label: "Ketentuan Program Loyalti Melaka",
    href: env.LOYALTY_POLICY_URL,
  },
];
